import { Tooltip, Typography } from '@material-ui/core';
import React, { FC, ReactElement, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useComplianceRequirements } from 'hooks/useComplianceRequirements';
import { usePostHog } from 'hooks/usePostHog';
import complianceAppIcon from 'images/compliance-app-icon.svg';

import messages from './messages';
import useStyles from './styles';

const STATUS_DISPLAY = {
  IN: { message: messages.compliant, dotClass: 'dotCompliant' },
  PENDING_REVIEW: {
    message: messages.pendingReview,
    dotClass: 'dotPendingReview',
  },
  OUT: { message: messages.notCompliant, dotClass: 'dotNotCompliant' },
} as const;

type RequirementStatus = keyof typeof STATUS_DISPLAY;

const statusDisplay = (status: string) =>
  STATUS_DISPLAY[status as RequirementStatus] ?? STATUS_DISPLAY.OUT;

export interface RequirementStatusEntry {
  uuid: string;
  status: string;
}

export interface ComplianceRequirementsTooltipProps {
  requirements: RequirementStatusEntry[];
  children: ReactElement;
}

const TooltipContent: FC<{ requirements: RequirementStatusEntry[] }> = ({
  requirements,
}) => {
  const styles = useStyles();
  const { requirements: catalog } = useComplianceRequirements();
  const names = new Map(catalog.map(entry => [entry.uuid, entry.name]));

  return (
    <div>
      <div className={styles.header}>
        <img className={styles.appIcon} src={complianceAppIcon} alt="" />
        <Typography component="span" className={styles.headerText}>
          <FormattedMessage {...messages.header} />
        </Typography>
      </div>
      <div className={styles.card}>
        <Typography className={styles.cardTitle}>
          <FormattedMessage
            {...messages.requirementsCount}
            values={{ count: requirements.length }}
          />
        </Typography>
        {requirements.map(requirement => {
          const display = statusDisplay(requirement.status);
          return (
            <div
              key={requirement.uuid}
              className={styles.row}
              data-testid="compliance-requirement-row"
            >
              <span className={`${styles.dot} ${styles[display.dotClass]}`} />
              <Typography component="span" className={styles.name}>
                {names.get(requirement.uuid) ?? (
                  <FormattedMessage {...messages.unknownRequirement} />
                )}
              </Typography>
              <Typography component="span" className={styles.status}>
                <FormattedMessage {...display.message} />
              </Typography>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const ComplianceRequirementsTooltip: FC<
  ComplianceRequirementsTooltipProps
> = ({ requirements, children }) => {
  const styles = useStyles();
  const posthog = usePostHog();
  const [open, setOpen] = useState(false);

  const handleMouseEnter = useCallback(() => {
    setOpen(wasOpen => {
      if (!wasOpen) {
        posthog.capture('compliance:status_label_tooltip_view', {
          requirement_count: requirements.length,
        });
      }
      return true;
    });
  }, [posthog, requirements.length]);

  const handleClose = useCallback(() => setOpen(false), []);

  if (!requirements.length) {
    return children;
  }

  return (
    <Tooltip
      arrow
      open={open}
      classes={{ tooltip: styles.tooltip, arrow: styles.arrow }}
      title={<TooltipContent requirements={requirements} />}
    >
      <div
        className={styles.trigger}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleClose}
        onClickCapture={handleClose}
      >
        {children}
      </div>
    </Tooltip>
  );
};

export default ComplianceRequirementsTooltip;
